import React from 'react'
import { Alert, Button, ButtonToolbar } from 'react-bootstrap'
import { CheckCircle2, AlertTriangle } from 'lucide-react'
import LinkAccountModal from 'sharedComponents/LinkAccountModal'
import { linkFromChannel } from 'account'
import { connect } from "react-redux"
import { changeApplication, setAccounts } from 'actions/rootActions'
import { setRedirectedAction, setBillingMessage } from 'actions/planActions'
import { simpleDispatchProps } from 'actions/actionUtils'
import { getCookie, clearTempCookies } from 'cookie'
import renderPlanAction from './planAction'
import { sessionStoragePayhelm } from 'modules/utils'
import NavLinkContainer from '../NavLinkContainer'
import { hasConnectedStore } from 'modules/storeTools'

@connect((store) => ({
  currentApp: store.root.applicationId,
  accounts: store.root.accounts,
  topUrl: store.root.topUrl,
  subscriptionId: store.plan.subscription ? store.plan.subscription.id : null,
  currentPlans: store.plan.subscription ? store.plan.subscription.plans : null,
  planStatus: store.plan.subscription ? store.plan.subscription.plan_status : null,
  planExpired: store.plan.subscription ? store.plan.subscription.plan_expired : false,
  billingMessage: store.plan.billingMessage,
  admin: store.root.admin,
  loginFromChannel: store.root.loginFromChannel,
  // #693: site-tools / no-connected-store users already get a "Connect a store"
  // CTA on SiteToolsLanding, so the "Link your account" banner is redundant.
  // Org / workspace-context users (who don't see that landing) still get it.
  siteToolsOnly:
    !hasConnectedStore(store.root.accounts) &&
    !store.root.user?.is_organization_workspace &&
    !store.root.user?.is_workspace_context,
}), (dispatch) => simpleDispatchProps(dispatch, { changeApplication, setRedirectedAction, setBillingMessage, setAccounts }))

export default class MarketingBanner extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      ...this.getInitialCallbackMsg(),
      linkModalOpenFor: '',
    }
  }

  getInitialCallbackMsg = () => {
    let callbackMsg = '', callbackMsgType = 'success', callbackMsgStay = false, callbackMsgDuration = 3000, visible = true, linking_types = ['Paypal Merchant', 'Paypal', 'Shopify']

    const [
      confirming_charge, charge_fulfilled,
      linking_account, linking_fulfilled, refreshing_token_for, new_linked_account,
      slack_fulfilled, teams_fulfilled, xcom_ads_error,
    ] = getCookie([
      'confirming_charge', 'charge_fulfilled',
      'linking_account', 'linking_fulfilled', 'refreshing_token_for', 'new_linked_account',
      'slack_fulfilled', 'teams_fulfilled', 'xcom_ads_error',
    ])

    if (confirming_charge) {
      if (charge_fulfilled) {
        if (charge_fulfilled === 'true') {
          callbackMsg = 'The billing charge has been confirmed.'
          callbackMsgType = 'success'
        }
        else {
          callbackMsg = 'The billing charge has been declined.'
          callbackMsgType = 'danger'
          callbackMsgStay = true
        }
      }
    }
    else if (linking_account) {
      if (linking_fulfilled) {
        if (linking_fulfilled === 'true') {
          if (refreshing_token_for) {
            if (new_linked_account) {
              const [merchant_id, remote_account_username] = decodeURIComponent(refreshing_token_for).split('|')
              const [new_account, channel] = decodeURIComponent(new_linked_account).split('|')
              callbackMsg = (
                <span>
                  {`You successfully linked account `}
                  <b>{new_account}</b>
                  {`. Please relink your ${channel} account `}
                  <b>{remote_account_username || merchant_id}</b>
                  {'.'}
                </span>
              )
            }
            else {
              callbackMsg = 'The account\'s token has been updated.'
            }
          }
          else {
            callbackMsg = 'The account linking has been done.'
          }
          callbackMsgType = 'success'
          // Give the success banner a longer auto-hide than the 3s default:
          // that was too brief on mobile after returning from an OAuth
          // round-trip, and users were re-running the link flow because
          // they doubted it had succeeded. It still auto-dismisses though,
          // and can be closed manually via the Alert's close button.
          callbackMsgDuration = 8000
        }
        else {
          if (xcom_ads_error === 'true') {
            callbackMsg = 'X Ads authorization was not completed. Please try again from the X Ads card.'
          }
          else {
            callbackMsg = refreshing_token_for ? 'Failed to update the account\'s token. Please try again.' : 'The account linking was not successful. Please try again.'
          }
          callbackMsgType = 'danger'
          callbackMsgStay = true
        }
      }
    }
    else if (slack_fulfilled) {
      if (slack_fulfilled === 'true') {
        callbackMsg = 'Slack linking has been done.'
        callbackMsgType = 'success'
      }
      else {
        callbackMsg = 'Slack linking was not successful. Please try again.'
        callbackMsgType = 'danger'
        callbackMsgStay = true
      }
    }
    else if (teams_fulfilled) {
      if (teams_fulfilled === 'true') {
        callbackMsg = 'Microsoft Teams linking has been done.'
        callbackMsgType = 'success'
      }
      else {
        callbackMsg = 'Microsoft Teams linking was not successful. Please try again.'
        callbackMsgType = 'danger'
        callbackMsgStay = true
      }
    }
    clearTempCookies()

    return { callbackMsg, callbackMsgType, callbackMsgStay, callbackMsgDuration, visible, linking_types }
  }

  componentDidMount() {
    this.scheduleCallbackMsgDismiss()
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.billingMessage.text && !prevProps.billingMessage.text) {
      this.setState({
        callbackMsg: this.props.billingMessage.text,
        callbackMsgType: this.props.billingMessage.type,
        callbackMsgStay: false,
        callbackMsgDuration: 3000,
      }, this.scheduleCallbackMsgDismiss)
      this.props.setBillingMessage({ text: '', type: ''})
      return
    }
    if (this.state.callbackMsg !== prevState.callbackMsg) {
      this.scheduleCallbackMsgDismiss()
    }
  }

  componentWillUnmount() {
    clearTimeout(this.callbackMsgTimeout)
  }

  scheduleCallbackMsgDismiss = () => {
    clearTimeout(this.callbackMsgTimeout)
    if (this.state.callbackMsg && !this.state.callbackMsgStay) {
      this.callbackMsgTimeout = setTimeout(() => {
        this.setState({ callbackMsg: '' })
      }, this.state.callbackMsgDuration || 3000)
    }
  }

  onClose = () => {
    sessionStoragePayhelm.ph_linked = 'true';
    this.setState({ visible: false });
  }

  render() {
    const { planStatus, planExpired, currentPlans, subscriptionId, accounts, thirdParty, topUrl, currentApp, admin, loginFromChannel, history, siteToolsOnly } = this.props
    const { callbackMsg, callbackMsgType, callbackMsgStay, visible, linking_types } = this.state
    let body = null, ph_linked = sessionStoragePayhelm.ph_linked
    if (callbackMsg) {
      const Icon = callbackMsgType === 'success' ? CheckCircle2 : AlertTriangle
      body = (
        <Alert
          variant={callbackMsgType}
          dismissible
          onClose={() => this.setState({ callbackMsg: '', callbackMsgStay: false })}
          className="d-flex align-items-center gap-2 shadow-sm border-0 rounded-3"
        >
          <Icon size={18} className="flex-shrink-0" />
          <span>{callbackMsg}</span>
        </Alert>
      )
    }
    else if (accounts.length === 0 && visible && !ph_linked && currentApp !== 'channels' && !siteToolsOnly ) {
      body = (
        <Alert variant={callbackMsgType} onClose={this.onClose}>
          Link your account <ButtonToolbar style={{float: 'right', marginTop: '-6px'}}>
          <NavLinkContainer to="/integrations" onClick={() => changeApplication(currentApp, 'integrations')} type="inline-block">
            <Button size="sm" variant="primary" style={{marginTop: 5, marginBottom: 5}}>Check Options</Button>
          </NavLinkContainer>
          </ButtonToolbar>
        </Alert>
      )
    }
    else if (planStatus && !thirdParty) {
      body = renderPlanAction(currentPlans, planStatus, planExpired, subscriptionId, setRedirectedAction, currentApp, changeApplication, loginFromChannel)
    }

    return (
      <div>
        { body }
        <LinkAccountModal />
      </div>
    )
  }
}